import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PhaseSidebar from './PhaseSidebar';
import FocusAreaSection from './FocusAreaSection';
import SubmitFooter from './SubmitFooter';
import ConfirmationModal from './ConfirmationModal';
import { useBcmAssessment } from '../../hooks/useBcmAssessment';

const PREVIEW_INCOMPLETE_COUNT = 6;
const DARK_MODE_KEY = 'bcm_dark_mode';

const BCMAssessmentPage = () => {
  const navigate = useNavigate();

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem(DARK_MODE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(DARK_MODE_KEY, JSON.stringify(darkMode));
  }, [darkMode]);

  const {
    phases,
    responses,
    selectedPhase,
    selectedPhaseId,
    setSelectedPhaseId,
    submissionStatus,
    loading,
    error,
    submitting,
    lastSaved,
    progress,
    incompleteQuestions,
    submissionState,
    handleResponse,
    handleEvidence,
    handleFilesSelected,
    retryPendingFile,
    removePendingFile,
    handleRemoveBackendFile,
    submitCurrentPhase,
    getPhaseProgress,
  } = useBcmAssessment(navigate);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState(null);
  const [showAllIncompleteQuestions, setShowAllIncompleteQuestions] = useState(false);
  const [isPhasePanelCollapsed, setIsPhasePanelCollapsed] = useState(false);

  const isLocked = selectedPhase && submissionStatus[selectedPhase.id] === 'submitted';

  const visibleIncompleteQuestions = useMemo(
    () =>
      showAllIncompleteQuestions
        ? incompleteQuestions
        : incompleteQuestions.slice(0, PREVIEW_INCOMPLETE_COUNT),
    [showAllIncompleteQuestions, incompleteQuestions]
  );

  const scrollToQuestion = useCallback((questionId) => {
    const el = document.getElementById(`question-${questionId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedQuestionId(questionId);
    setTimeout(() => {
      setHighlightedQuestionId((prev) => (prev === questionId ? null : prev));
    }, 2000);
  }, []);

  const theme = {
    page: darkMode ? 'bg-gray-950 text-gray-100' : 'bg-white text-gray-900',
    panel: darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white',
    panelMuted: darkMode ? 'bg-gray-800 border-gray-700' : 'bg-amber-50',
    heading: darkMode ? 'text-amber-300' : 'text-amber-800',
    title: darkMode ? 'text-amber-400' : 'text-amber-700',
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    mutedText: darkMode ? 'text-gray-300' : 'text-gray-700',
    questionCard: darkMode ? 'bg-gray-900 border border-gray-800 hover:shadow-lg' : 'bg-white hover:shadow-lg',
    lockedCard: darkMode ? 'bg-gray-900 border border-gray-800 opacity-75' : 'bg-gray-50 opacity-75',
    input:
      darkMode
        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500'
        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-amber-500 focus:border-amber-500',
    disabledInput: darkMode
      ? 'bg-gray-800 cursor-not-allowed border-gray-700 text-gray-400'
      : 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-500',
    progressTrack: darkMode ? 'bg-gray-700' : 'bg-gray-300',
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme.page}`}>
        <div className={`rounded-2xl shadow-xl p-8 text-center ${theme.panel}`}>
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${theme.title}`}>Loading BCM Assessment</h2>
          <p className={theme.subtext}>Please wait while we prepare your assigned phases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${theme.page}`}>
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme.page}`}>
      <div className="max-w-5xl mx-auto px-4 py-8 mb-16">
        <div className="flex justify-between items-center mb-6 gap-3">
          <div className="w-24" />
          <h1 className={`text-3xl font-bold text-center ${theme.title}`}>BCM Assessment</h1>

          <div className="w-24 flex justify-end">
            <button
              type="button"
              onClick={() => setDarkMode((prev) => !prev)}
              className={`relative inline-flex h-11 w-24 items-center rounded-full transition-all duration-300 border shadow-sm ${
                darkMode ? 'bg-gray-800 border-gray-600' : 'bg-amber-100 border-amber-300'
              }`}
              aria-label="Toggle dark mode"
            >
              <span
                className={`absolute left-1 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md transition-transform duration-300 ${
                  darkMode ? 'translate-x-[52px]' : 'translate-x-0'
                }`}
              >
                {darkMode ? '🌙' : '☀️'}
              </span>
              <span
                className={`w-full text-xs font-bold ${
                  darkMode ? 'text-gray-200 pl-3 text-left' : 'text-amber-800 pr-3 text-right'
                }`}
              >
                {darkMode ? 'DARK' : 'LIGHT'}
              </span>
            </button>
          </div>
        </div>

        {selectedPhase && (
          <PhaseSidebar
            phases={phases}
            selectedPhaseId={selectedPhaseId}
            onSelectPhase={setSelectedPhaseId}
            getPhaseProgress={getPhaseProgress}
            submissionStatus={submissionStatus}
            isCollapsed={isPhasePanelCollapsed}
            setIsCollapsed={setIsPhasePanelCollapsed}
            selectedProgress={progress}
            isLocked={isLocked}
            darkMode={darkMode}
            theme={theme}
          />
        )}

        {selectedPhase && (
          <>
            {(selectedPhase.focus_areas || []).map((focusArea) => (
              <FocusAreaSection
                key={focusArea.id}
                focusArea={focusArea}
                responses={responses}
                isLocked={isLocked}
                highlightedQuestionId={highlightedQuestionId}
                onAnswerChange={handleResponse}
                onEvidenceChange={handleEvidence}
                onFilesSelected={handleFilesSelected}
                onRemoveBackendFile={handleRemoveBackendFile}
                onRetryPendingFile={retryPendingFile}
                onRemovePendingFile={removePendingFile}
                darkMode={darkMode}
                theme={theme}
              />
            ))}

            {!isLocked && incompleteQuestions.length > 0 && (
              <div className={`mb-5 rounded-lg border p-4 ${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className={`font-semibold ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                    ⚠️ {incompleteQuestions.length} question{incompleteQuestions.length !== 1 ? 's' : ''} still incomplete
                  </p>
                  {incompleteQuestions.length > PREVIEW_INCOMPLETE_COUNT && (
                    <button
                      type="button"
                      onClick={() => setShowAllIncompleteQuestions((prev) => !prev)}
                      className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:opacity-90"
                    >
                      {showAllIncompleteQuestions ? 'Show less' : `Show all (${incompleteQuestions.length})`}
                    </button>
                  )}
                </div>

                <div className="mt-3 space-y-2 max-h-60 overflow-auto">
                  {visibleIncompleteQuestions.map((item) => (
                    <button
                      key={item.questionId}
                      type="button"
                      onClick={() => scrollToQuestion(item.questionId)}
                      className={`w-full text-left p-2 rounded border text-sm transition ${
                        darkMode
                          ? 'border-gray-700 bg-gray-900 hover:bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-800'
                      }`}
                    >
                      <div>
                        <span className="font-semibold">Q{item.order}</span> — {item.text}
                      </div>
                      <div className="text-xs mt-1 text-red-400">{item.reason}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <SubmitFooter
              isLocked={isLocked}
              submitting={submitting}
              canSubmit={submissionState.canSubmit}
              submitReason={submissionState.reason}
              pendingUploads={submissionState.pendingUploads}
              onOpenConfirm={() => setShowConfirmModal(true)}
              onGoDashboard={() => navigate('/bcm/dashboard')}
              lastSaved={lastSaved}
              theme={theme}
            />
          </>
        )}

        <ConfirmationModal
          open={showConfirmModal}
          phaseTitle={selectedPhase?.title}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={async () => {
            if (!submissionState.canSubmit) return;
            setShowConfirmModal(false);
            await submitCurrentPhase();
          }}
          darkMode={darkMode}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default BCMAssessmentPage;